/**
 * Protected Route Component
 * Handles authentication-based route protection with loading states
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean; // legacy
  requireRoles?: string[]; // new: list of roles (user_type or roles claim)
  fallback?: React.ReactNode; // optional custom denial UI
}

/**
 * Component that protects routes from unauthorized access
 * Redirects to signin if not authenticated
 * Shows loading spinner while checking authentication status
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAdmin = false,
  requireRoles = [],
  fallback
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-gray-500">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to signin if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate 
        to="/signin" 
        state={{ from: location }} 
        replace 
      />
    );
  }

  // Admin privilege check - ONLY allow admin/superuser (is_staff must be true)
  if (requireAdmin && user) {
    // Explicitly check for admin privileges
    const isAdmin = user?.is_superuser === true;
    const isStaff = user?.is_staff === true;
    const isLibrarian = user?.user_type === 'librarian' || user?.role === 'librarian';
    
    // Reject librarians, students, and any non-staff users
    if (isLibrarian || !isStaff) {
      return fallback || (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Access Denied</h2>
            <p className="mt-2 text-gray-500">Administrator access required. Only admin/superuser accounts can access this page.</p>
          </div>
        </div>
      );
    }
  }

  // Role-based check (supports either user.user_type or user.roles array)
  if (requireRoles.length > 0 && user) {
    const userType = user.user_type || user.role;
    const multiRoles: string[] = Array.isArray(user.roles) ? user.roles : [];
    const hasRequired = requireRoles.some(r => r === userType || multiRoles.includes(r));
    if (!hasRequired) {
      return fallback || (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Access Denied</h2>
            <p className="mt-2 text-gray-500">You lack the required role: {requireRoles.join(', ')}</p>
          </div>
        </div>
      );
    }
  }

  // Render protected content
  return <>{children}</>;
};

export default ProtectedRoute;
